<script setup>
const { locale, t } = useI18n()
const route = useRoute()
const router = useRouter()

// Blog articles use a different slug per language, so the generic '/en'
// prefix add/strip below would point at a slug that doesn't exist in the
// target language. Resolve the real alternate-language slug from the blog
// manifest instead. Uses useAsyncData (not top-level await) so this stays
// a plain sync setup — Nuxt still awaits it during SSR via onServerPrefetch,
// and `watch` refetches when navigating between articles client-side.
const BLOG_MANIFEST_URL = 'https://raw.githubusercontent.com/Salamis-Bay-Conti-Resort-Hotel/salamis-blog/main/public/articles/manifest.json'

const blogSlugMatch = computed(() => {
  const m = route.path.match(/^\/(en\/)?blog\/([^/]+?)(?:\.html)?$/)
  if (!m) return null
  return { lang: m[1] ? 'en' : 'tr', slug: decodeURIComponent(m[2]) }
})

const { data: blogLangPaths } = useAsyncData(
  'site-header-blog-lang-paths',
  async () => {
    const match = blogSlugMatch.value
    if (!match) return null
    try {
      const manifest = await $fetch(BLOG_MANIFEST_URL, { responseType: 'json' })
      const otherLang = match.lang === 'en' ? 'tr' : 'en'
      const entry = Array.isArray(manifest)
        ? manifest.find((item) => item?.[match.lang]?.slug === match.slug)
        : null
      const otherSlug = entry?.[otherLang]?.slug
      if (!otherSlug) return null
      const trSlug = match.lang === 'tr' ? match.slug : otherSlug
      const enSlug = match.lang === 'en' ? match.slug : otherSlug
      return { tr: `/blog/${trSlug}`, en: `/en/blog/${enSlug}` }
    } catch {
      return null
    }
  },
  { watch: [() => route.path] }
)

const getBasePath = () => {
  const currentPath = route.path
  if (currentPath.startsWith('/en/')) {
    return currentPath.replace('/en', '')
  } else if (currentPath === '/en') {
    return '/'
  }
  return currentPath
}

const getTrUrl = () => blogLangPaths.value?.tr ?? (getBasePath() || '/')
const getEnUrl = () => {
  if (blogLangPaths.value?.en) return blogLangPaths.value.en
  const basePath = getBasePath()
  return basePath === '/' ? '/en' : `/en${basePath}`
}

// Navigate directly to the resolved URL; Nuxt i18n's route middleware detects
// the locale from the URL prefix on arrival (same pattern as the plain
// NuxtLinks elsewhere in this file). Calling setLocale() here as well would
// trigger the module's own same-slug route switch first — which 404s on blog
// articles, since their slug differs per language — racing with this push.
const switchLanguage = (lang) => {
  if (locale.value === lang) return
  const target = lang === 'en' ? getEnUrl() : getTrUrl()
  router.push(target)
}

const searchOpen = ref(false)
const searchLoaded = ref(false)
const openSearch = () => {
  searchLoaded.value = true
  searchOpen.value = true
}
const drawerOpen = ref(false)
const aboutOpen = ref(false)
const galleryMenuOpen = ref(false)
const aboutWrapRef = ref(null)
const scrolled = ref(false)
const bannerOffset = ref(0)
const scrollY = ref(0)
// The banner is position:absolute (scrolls away with the page), but the
// header is position:fixed (doesn't). Without this, the header would sit
// pinned at `bannerOffset` forever, leaving a blank gap where the banner
// used to be once it scrolls out of view. Shrinking the offset 1:1 with
// scroll makes the header appear to "catch up" to the banner's trailing
// edge, closing the gap exactly as the banner leaves.
const headerTop = computed(() => Math.max(0, bannerOffset.value - scrollY.value))

let _removeScroll = () => {}
let _disconnectObserver = () => {}
let _removeOutsideClick = () => {}

// smart-app-banner.js only loads a few seconds after mount (deferred for
// perf), so bannerOffset used to sit at 0 and then jump once it finally
// appeared — visible as the whole header lurching down. Guess the same
// 80px it renders at (public/js/smart-app-banner.css) right away so the
// header reserves the space up front; the MutationObserver below still
// corrects it to the real height once the banner actually mounts.
const guessBannerOffset = () => {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return 0
  const alreadyHandled = /smartbanner-closed|smartbanner-installed/.test(document.cookie)
  if (alreadyHandled) return 0
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone
  if (isStandalone) return 0
  const isMobile = /iPhone|iPad|iPod|Android/.test(navigator.userAgent)
  return isMobile ? 80 : 0
}

onMounted(() => {
  bannerOffset.value = guessBannerOffset()

  let _prevY = -1
  const onScroll = () => {
    const y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
    if (y === _prevY) return
    _prevY = y
    scrolled.value = y > 20
    scrollY.value = y
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  document.addEventListener('scroll', onScroll, { passive: true })
  _removeScroll = () => {
    window.removeEventListener('scroll', onScroll)
    document.removeEventListener('scroll', onScroll)
  }

  const updateOffset = () => {
    const banner = document.querySelector('.smartbanner')
    bannerOffset.value = (banner && banner.offsetHeight > 0) ? banner.offsetHeight : 0
  }

  const observer = new MutationObserver(updateOffset)
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] })
  // smart-app-banner.js toggles its "smartbanner-show" class on <html>
  // (document.documentElement) when closed, not on <body> or the banner
  // element itself — that's the banner's own PARENT, outside the subtree
  // above, so it needs its own (non-subtree) watch or closing never
  // re-triggers updateOffset and the header stays offset forever.
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
  updateOffset()
  _disconnectObserver = () => observer.disconnect()

  const onOutsideClick = (e) => {
    if (aboutOpen.value && aboutWrapRef.value && !aboutWrapRef.value.contains(e.target)) {
      aboutOpen.value = false
    }
  }
  document.addEventListener('click', onOutsideClick)
  _removeOutsideClick = () => document.removeEventListener('click', onOutsideClick)
})

onUnmounted(() => {
  _removeScroll()
  _disconnectObserver()
  _removeOutsideClick()
})

const r = (route) => locale.value === 'en' ? `/en/${route}` : `/${route}`
const openReservation = () =>
  window.open('https://salamis-bay-conti-hotel.hotelrunner.com/bv3/search', '_blank')

const weather = ref(null)

const weatherIcon = computed(() => {
  const c = weather.value?.code
  if (c === undefined) return null
  if (c === 0) return 'sun'
  if (c <= 3) return 'cloud-sun'
  if (c <= 48) return 'cloud'
  if (c <= 67) return 'rain'
  if (c <= 77) return 'snow'
  if (c <= 82) return 'rain'
  return 'thunder'
})

const seaTemp = ref(null)

onMounted(async () => {
  try {
    const [airRes, seaRes] = await Promise.all([
      fetch('https://api.open-meteo.com/v1/forecast?latitude=35.198&longitude=33.884&current=temperature_2m,weather_code&timezone=auto'),
      fetch('https://marine-api.open-meteo.com/v1/marine?latitude=35.198&longitude=33.884&current=sea_surface_temperature&timezone=auto')
    ])
    const [airData, seaData] = await Promise.all([airRes.json(), seaRes.json()])
    weather.value = { temp: Math.round(airData.current.temperature_2m), code: airData.current.weather_code }
    seaTemp.value = Math.round(seaData.current.sea_surface_temperature)
  } catch {}
})
</script>

<template>
  <div class="sh-wrap" :class="{ scrolled }" :style="{ top: headerTop + 'px' }">
    <!-- Topbar -->
    <div class="sh-topbar">
      <div class="sh-topbar-left">
        <a href="tel:+908507070101" class="sh-tlink sh-tlink--phone">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          <span class="sh-phone-lines">
            <span class="sh-phone-row">+90 850 707 01 01 <span class="sh-phone-tag">{{ $t('phone_free_international') }}</span></span>
            <span class="sh-phone-row"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>0850 707 01 01 <span class="sh-phone-tag">{{ $t('phone_free_kktc') }}</span></span>
          </span>
        </a>
        <a href="mailto:onlinecc@salamisbayconti.com" class="sh-tlink">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
          onlinecc@salamisbayconti.com
        </a>
      </div>
      <div class="sh-topbar-right">
        <button class="sh-search-btn" @click="openSearch" :aria-label="locale === 'en' ? 'Search' : 'Ara'">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <span class="sh-search-btn-label">{{ locale === 'en' ? 'Search' : 'Ara' }}</span>
        </button>
        <div v-if="weather" class="sh-weather">
          <!-- sun -->
          <svg v-if="weatherIcon === 'sun'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/></svg>
          <!-- cloud-sun -->
          <svg v-else-if="weatherIcon === 'cloud-sun'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v2M4.93 4.93l1.41 1.41M2 12h2M19.07 4.93l-1.41 1.41"/><circle cx="12" cy="9" r="3"/><path d="M17 14a5 5 0 0 0-10 0H6a3 3 0 0 0 0 6h11a3 3 0 0 0 0-6h-.5"/></svg>
          <!-- cloud -->
          <svg v-else-if="weatherIcon === 'cloud'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>
          <!-- rain -->
          <svg v-else-if="weatherIcon === 'rain'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><path d="M13 19v3M7 19v3M19 19v3"/></svg>
          <!-- snow -->
          <svg v-else-if="weatherIcon === 'snow'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><path d="M13 20v1M7 20v1M19 20v1"/></svg>
          <!-- thunder -->
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><path d="M13 12l-3 6h5l-3 6"/></svg>
          <span class="sh-weather-temp">{{ weather.temp }}°C</span>
        </div>
        <div v-else class="sh-weather sh-weather-skeleton" aria-hidden="true">
          <span class="sh-skel-icon"></span>
          <span class="sh-skel-text"></span>
        </div>
        <div v-if="seaTemp" class="sh-weather sh-weather--sea">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2"/></svg>
          <span class="sh-weather-temp">{{ seaTemp }}°C</span>
        </div>
        <div v-else class="sh-weather sh-weather-skeleton" aria-hidden="true">
          <span class="sh-skel-icon"></span>
          <span class="sh-skel-text"></span>
        </div>
        <span class="sh-topbar-sep">|</span>
        <div class="sh-lang">
          <a :href="getTrUrl()" :class="{ on: locale === 'tr' }" @click.prevent="switchLanguage('tr')">TR</a>
          <span class="sh-lang-sep">|</span>
          <a :href="getEnUrl()" :class="{ on: locale === 'en' }" @click.prevent="switchLanguage('en')">EN</a>
        </div>
      </div>
    </div>

    <!-- Header row -->
    <div class="sh-hdr">
      <NuxtLink :to="locale === 'en' ? '/en' : '/'" class="sh-logo">
        <img
          src="https://api2.salamisbayconti.com/storage/logosalamis.png"
          alt="Salamis Bay Conti Resort Hotel"
          height="54"
          width="117"
          style="width: auto"
          fetchpriority="high"
        />
      </NuxtLink>

      <nav class="sh-nav">
        <NuxtLink :to="r('rooms')" class="sh-navlink">{{ $t('Rooms') }}</NuxtLink>
        <NuxtLink :to="r('flavors')" class="sh-navlink">{{ $t('Flavors') }}</NuxtLink>
        <NuxtLink :to="r('spa')" class="sh-navlink">{{ $t('Spa') }}</NuxtLink>
        <NuxtLink :to="r('events')" class="sh-navlink">{{ $t('Events') }}</NuxtLink>
        <div class="sh-navwrap" ref="aboutWrapRef">
          <NuxtLink :to="r('about')" class="sh-navlink" :class="{ 'sh-navlink--active': aboutOpen }">
            {{ $t('About') }}
            <span class="sh-caret"></span>
          </NuxtLink>
          <div class="sh-dropdown" :class="{ 'sh-dropdown--open': aboutOpen }">
            <NuxtLink :to="r('concepts/summer')" @click="aboutOpen = false">
              <span class="sh-dt">{{ $t('summer-concept') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('concepts/winter')" @click="aboutOpen = false">
              <span class="sh-dt">{{ $t('winter-concept') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('faqs')" @click="aboutOpen = false">
              <span class="sh-dt">{{ $t('FAQ') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('about')" @click="aboutOpen = false">
              <span class="sh-dt">{{ $t('About') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('blog')" @click="aboutOpen = false">
              <span class="sh-dt">{{ locale === 'tr' ? 'Kıbrıs Rehberi' : 'Cyprus Guide' }}</span>
            </NuxtLink>
          </div>
        </div>
        <div class="sh-navwrap">
          <NuxtLink :to="r('gallery')" class="sh-navlink">
            {{ $t('gallery') }}
            <span class="sh-caret"></span>
          </NuxtLink>
          <div class="sh-dropdown">
            <NuxtLink :to="r('gallery') + '#restaurants'">
              <span class="sh-dt">{{ $t('gallery_restaurants') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('gallery') + '#rooms'">
              <span class="sh-dt">{{ $t('Rooms') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('gallery') + '#activities'">
              <span class="sh-dt">{{ $t('gallery_events') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('gallery') + '#meetings'">
              <span class="sh-dt">{{ $t('meeting_saloon') }}</span>
            </NuxtLink>
            <NuxtLink :to="r('gallery') + '#spa'">
              <span class="sh-dt">{{ $t('Spa & Pools') }}</span>
            </NuxtLink>
          </div>
        </div>
        <NuxtLink :to="r('contact')" class="sh-navlink">{{ $t('Contact') }}</NuxtLink>
      </nav>

      <div class="sh-hdr-right">
        <button class="sh-search-btn sh-search-btn--hdr" @click="openSearch" :aria-label="locale === 'en' ? 'Search' : 'Ara'">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        </button>
        <button class="sh-btn-reserve" @click="openReservation">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
          {{ $t('book_now') }}
        </button>
        <button class="sh-hamburger" @click="drawerOpen = true" aria-label="Menu">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
        </button>
      </div>
    </div>

    <!-- Mobile Drawer -->
    <Teleport to="body">
      <div class="sh-drawer" :class="{ open: drawerOpen }" :style="{ '--banner-h': headerTop + 'px' }" @click.self="drawerOpen = false">
        <div class="sh-drawer-panel">
          <div class="sh-drawer-head">
            <div v-if="weather || seaTemp" class="sh-drawer-weather">
              <div v-if="weather" class="sh-drawer-weather-item">
                <svg v-if="weatherIcon === 'sun'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/></svg>
                <svg v-else-if="weatherIcon === 'cloud-sun'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v2M4.93 4.93l1.41 1.41M2 12h2M19.07 4.93l-1.41 1.41"/><circle cx="12" cy="9" r="3"/><path d="M17 14a5 5 0 0 0-10 0H6a3 3 0 0 0 0 6h11a3 3 0 0 0 0-6h-.5"/></svg>
                <svg v-else-if="weatherIcon === 'cloud'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>
                <svg v-else-if="weatherIcon === 'rain'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><path d="M13 19v3M7 19v3M19 19v3"/></svg>
                <svg v-else-if="weatherIcon === 'snow'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><path d="M13 20v1M7 20v1M19 20v1"/></svg>
                <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><path d="M13 12l-3 6h5l-3 6"/></svg>
                <span>{{ weather.temp }}°C</span>
              </div>
              <div v-if="seaTemp" class="sh-drawer-weather-item sh-drawer-weather-sea">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2"/></svg>
                <span>{{ seaTemp }}°C</span>
              </div>
            </div>
            <button class="sh-drawer-x" @click="drawerOpen = false" aria-label="Close">
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </div>
          <nav class="sh-drawer-nav">
            <button class="sh-drawer-search-item" @click="drawerOpen = false; openSearch()">
              {{ locale === 'en' ? 'Search' : 'Ara' }}
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </button>
            <NuxtLink :to="r('rooms')" @click="drawerOpen = false">{{ $t('Rooms') }}<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></NuxtLink>
            <NuxtLink :to="r('flavors')" @click="drawerOpen = false">{{ $t('Flavors') }}<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></NuxtLink>
            <NuxtLink :to="r('spa')" @click="drawerOpen = false">{{ $t('Spa') }}<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></NuxtLink>
            <NuxtLink :to="r('events')" @click="drawerOpen = false">{{ $t('Events') }}<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></NuxtLink>
            <div class="sh-drawer-accordion">
              <button class="sh-drawer-accordion-trigger" @click.stop="aboutOpen = !aboutOpen">
                {{ $t('About') }}
                <svg class="sh-drawer-accordion-caret" :class="{ open: aboutOpen }" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </button>
              <div class="sh-drawer-accordion-body" :class="{ open: aboutOpen }">
                <div>
                  <NuxtLink :to="r('concepts/summer')" @click="drawerOpen = false; aboutOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('summer-concept') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('concepts/winter')" @click="drawerOpen = false; aboutOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('winter-concept') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('faqs')" @click="drawerOpen = false; aboutOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('FAQ') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('about')" @click="drawerOpen = false; aboutOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('About') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('blog')" @click="drawerOpen = false; aboutOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ locale === 'tr' ? 'Kıbrıs Rehberi' : 'Cyprus Guide' }}</span>
                  </NuxtLink>
                </div>
              </div>
            </div>
            <div class="sh-drawer-accordion">
              <button class="sh-drawer-accordion-trigger" @click.stop="galleryMenuOpen = !galleryMenuOpen">
                {{ $t('gallery') }}
                <svg class="sh-drawer-accordion-caret" :class="{ open: galleryMenuOpen }" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </button>
              <div class="sh-drawer-accordion-body" :class="{ open: galleryMenuOpen }">
                <div>
                  <NuxtLink :to="r('gallery')" @click="drawerOpen = false; galleryMenuOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('gallery_general') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('gallery') + '#restaurants'" @click="drawerOpen = false; galleryMenuOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ locale === 'tr' ? 'Restorant & Bar' : 'Restaurants & Bars' }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('gallery') + '#rooms'" @click="drawerOpen = false; galleryMenuOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('Rooms') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('gallery') + '#activities'" @click="drawerOpen = false; galleryMenuOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('Events') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('gallery') + '#meetings'" @click="drawerOpen = false; galleryMenuOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('meeting_saloon') }}</span>
                  </NuxtLink>
                  <NuxtLink :to="r('gallery') + '#spa'" @click="drawerOpen = false; galleryMenuOpen = false" class="sh-drawer-sub-link">
                    <span class="sh-drawer-sub-title">{{ $t('Spa & Pools') }}</span>
                  </NuxtLink>
                </div>
              </div>
            </div>
            <NuxtLink :to="r('contact')" @click="drawerOpen = false">{{ $t('Contact') }}<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></NuxtLink>
            <NuxtLink :to="r('career')" @click="drawerOpen = false">{{ $t('career') }}<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></NuxtLink>
            <button class="sh-drawer-resv-item" @click="openReservation(); drawerOpen = false">
              {{ $t('book_now') }}
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
            </button>
            <div class="sh-drawer-lang-item">
              <a :href="getTrUrl()" :class="{ on: locale === 'tr' }" @click.prevent="switchLanguage('tr'); drawerOpen = false">TR</a>
              <span class="sh-drawer-lang-sep">|</span>
              <a :href="getEnUrl()" :class="{ on: locale === 'en' }" @click.prevent="switchLanguage('en'); drawerOpen = false">EN</a>
            </div>
          </nav>
        </div>
      </div>
    </Teleport>

    <LazySiteSearch v-if="searchLoaded" v-model="searchOpen" />
  </div>
</template>

<style scoped>
/* ─── Tokens ────────────────────────────────────────────── */
.sh-wrap {
  --gold:      #E7BB7F;
  --gold-dark: #C8993A;
  --navy:      #042333;
  --cream:     #FDFAF5;
  --cream-2:   #F0E8D8;
  --font-serif: 'Cormorant Garamond', Georgia, serif;
  --font-sans:  'Cormorant Garamond', Georgia, serif;
  --radius-sm: 4px;
  --radius-md: 8px;
  --dur-fast: 160ms;
  --dur-base: 280ms;
  --ease-out: cubic-bezier(.22,.61,.36,1);
}

/* ─── Wrapper ────────────────────────────────────────────── */
.sh-wrap {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 200;
  font-family: var(--font-sans);
}
.sh-wrap::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  bottom: -56px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0) 100%);
  backdrop-filter: blur(18px) saturate(120%);
  -webkit-backdrop-filter: blur(18px) saturate(120%);
  mask-image: linear-gradient(to bottom, black 0%, black 60%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 0%, black 60%, transparent 100%);
  pointer-events: none;
  z-index: -1;
}
/* ─── Topbar ─────────────────────────────────────────────── */
.sh-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  max-width: 1300px;
  width: 100%;
  margin-inline: auto;
  padding: 9px 0px;
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.sh-topbar-left { display: flex; align-items: center; gap: 22px; }
.sh-tlink {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: rgba(255,255,255,.8);
  text-decoration: none;
  font-size: 13px;
  letter-spacing: .04em;
  transition: color var(--dur-fast);
}
.sh-tlink svg { color: var(--gold); flex-shrink: 0; }
.sh-tlink:hover { color: #fff; }
.sh-tlink--phone { align-items: center; }
.sh-phone-lines { display: flex; flex-direction: row; gap: 14px; }
.sh-phone-row { display: flex; align-items: center; gap: 4px; font-size: 16px; font-weight: 500;  }
.sh-phone-tag {
  font-size: 10px;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: var(--gold);
  opacity: .85;
  position: relative;
  top: 2px;
}
.sh-topbar-right { display: flex; align-items: center; gap: 12px; }
.sh-weather { display: flex; align-items: center; gap: 5px; color: rgba(255,255,255,.8); }
.sh-weather svg { color: var(--gold); flex-shrink: 0; }
.sh-weather--sea svg { color: #7ecde8; }
.sh-weather-temp { font-size: 13px; letter-spacing: .03em; }
.sh-topbar-sep { color: rgba(255,255,255,.3); font-size: 12px; }

/* Reserves the same width/height as the real weather badges so the
   topbar doesn't shift when the async open-meteo fetch resolves. */
.sh-skel-icon {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: rgba(255,255,255,.2);
  flex-shrink: 0;
}
.sh-skel-text {
  width: 28px;
  height: 11px;
  border-radius: 3px;
  background: rgba(255,255,255,.2);
}

/* ─── Lang switcher ─────────────────────────────────────── */
.sh-lang { display: flex; align-items: center; gap: 7px; font-size: 13px; letter-spacing: .08em; }
.sh-lang a {
  background: none; border: 0;
  color: rgba(255,255,255,.55);
  font-family: var(--font-sans); font-size: 13px; letter-spacing: .08em;
  cursor: pointer; padding: 2px 4px;
  text-decoration: none;
  transition: color var(--dur-fast);
}
.sh-lang a.on { color: var(--gold); font-weight: 600; }
.sh-lang a:hover { color: #fff; }
.sh-lang-sep { color: rgba(255,255,255,.3); }

/* ─── Header row ─────────────────────────────────────────── */
.sh-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(20px, 3vw, 56px);
  max-width: 1370px;
  width: 100%;
  margin-inline: auto;
  padding: 10px 32px;
}
.sh-logo { display: flex; align-items: center; text-decoration: none; flex: none; margin-right: auto; }
.sh-logo img {
  height: clamp(48px, 5vw, 70px);
  width: auto;
  display: block;
}

/* ─── Nav ─────────────────────────────────────────────────── */
.sh-nav { display: flex; align-items: center; gap: clamp(12px, 1.4vw, 22px); }
.sh-navlink {
  position: relative;
  font-weight: 600; font-size: 14px; letter-spacing: .14em; text-transform: uppercase;
  color: #fff; text-decoration: none; padding: 6px 0; white-space: nowrap; cursor: pointer;
  display: inline-flex; align-items: center; gap: 5px;
  background: none; border: 0; font-family: var(--font-sans);
  transition: color var(--dur-fast) var(--ease-out);
}
.sh-navlink::after {
  content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 2px;
  background: var(--gold); transform: scaleX(0); transform-origin: left;
  transition: transform var(--dur-base) var(--ease-out);
}
.sh-navlink:hover, .router-link-active.sh-navlink, .sh-navlink--active { color: var(--gold); }
.sh-navlink:hover::after, .router-link-active.sh-navlink::after, .sh-navlink--active::after { transform: scaleX(1); }

.sh-caret {
  display: inline-block; width: 6px; height: 6px;
  border-right: 1.6px solid currentColor; border-bottom: 1.6px solid currentColor;
  transform: rotate(45deg) translateY(-2px); opacity: .72;
  transition: transform var(--dur-fast);
}
.sh-navwrap { position: relative; }
.sh-navwrap:hover .sh-caret, .sh-navlink--active .sh-caret { transform: rotate(225deg) translateY(2px); }

/* ─── Dropdown ───────────────────────────────────────────── */
.sh-dropdown {
  position: absolute; top: calc(100% + 14px); left: 50%;
  transform: translateX(-50%) translateY(8px);
  min-width: 240px; background: var(--cream);
  border-radius: var(--radius-md); box-shadow: 0 24px 60px rgba(4,35,51,.18);
  padding: 8px; opacity: 0; visibility: hidden;
  transition: all var(--dur-base) var(--ease-out); z-index: 80;
}
.sh-navwrap:hover .sh-dropdown, .sh-dropdown--open { opacity: 1; visibility: visible; transform: translateX(-50%) translateY(0); }
.sh-dropdown::before {
  content: ""; position: absolute; top: -7px; left: 50%;
  transform: translateX(-50%) rotate(45deg); width: 14px; height: 14px; background: var(--cream);
}
.sh-dropdown::after {
  /* Invisible bridge over the gap between the trigger and the dropdown box —
     without it, the mouse leaves .sh-navwrap's hoverable area while crossing
     the gap on a real (non-instant) mouse path, closing the dropdown early. */
  content: ""; position: absolute; top: -14px; left: 0; right: 0; height: 14px;
}
.sh-dropdown a { display: flex; flex-direction: column; gap: 3px; padding: 11px 14px; border-radius: 4px; text-decoration: none; transition: background var(--dur-fast); }
.sh-dropdown a:hover { background: var(--cream-2); }
.sh-dt { font-family: var(--font-serif); font-weight: 600; font-size: 16px; color: var(--navy); line-height: 1.1; }
.sh-dd { font-size: 11px; color: var(--navy); letter-spacing: .02em; opacity: .55; }

/* ─── Right actions ──────────────────────────────────────── */
.sh-hdr-right { display: flex; align-items: center; gap: clamp(10px, 1.4vw, 18px); flex: none; margin-left: -20px; }
.sh-btn-reserve {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font-family: var(--font-sans); font-weight: 700; font-size: 12px;
  letter-spacing: .13em; text-transform: uppercase;
  color: var(--navy); cursor: pointer; background: var(--gold);
  border: 0; border-radius: var(--radius-sm); padding: 11px 18px;
  transition: background var(--dur-base), box-shadow var(--dur-base);
  white-space: nowrap;
  /* Locked so an i18n/font swap on hydration never resizes the button —
     that resize was the "shake" users saw next to the header logo. */
  min-width: 172px;
  flex-shrink: 0;
}
.sh-btn-reserve:hover { background: var(--gold-dark); color: #fff; box-shadow: 0 2px 8px rgba(4,35,51,.15); }
.sh-btn-reserve svg { flex-shrink: 0; }

.sh-hamburger {
  display: none; width: 48px; height: 48px;
  margin-bottom: 5px;
  border: none; border-radius: var(--radius-sm);
  background: none; color: #fff; cursor: pointer;
  align-items: center; justify-content: center;
}

/* ─── Mobile drawer ──────────────────────────────────────── */
:global(.sh-drawer) {
  position: fixed; inset: 0; z-index: 300;
  background: rgba(4,35,51,.55); opacity: 0; visibility: hidden;
  transition: opacity 400ms ease; cursor: pointer;
}
:global(.sh-drawer-panel) * { cursor: default; }
:global(.sh-drawer.open) { opacity: 1; visibility: visible; }
:global(.sh-drawer-panel) {
  position: absolute; top: var(--banner-h, 0px); right: 0; height: calc(100% - var(--banner-h, 0px)); width: min(70vw, 260px);
  background: rgba(4,35,51,.94);
  backdrop-filter: blur(24px) saturate(120%);
  -webkit-backdrop-filter: blur(24px) saturate(120%);
  border-left: 1px solid rgba(231,187,127,.15);
  transform: translateX(100%);
  transition: transform 280ms cubic-bezier(.22,.61,.36,1);
  display: flex; flex-direction: column; padding: 22px; overflow-y: auto;
}
:global(.sh-drawer.open .sh-drawer-panel) { transform: translateX(0); }
:global(.sh-drawer-head) { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
:global(.sh-drawer-x) {
  width: 30px; height: 30px; border: none;
  border-radius: 4px; background: none; color: rgba(253,250,245,.7); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
:global(.sh-drawer-nav) { display: flex; flex-direction: column; margin-top: 8px; }
:global(.sh-drawer-nav a) {
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; font-weight: 600; color: rgba(253,250,245,.8);
  text-decoration: none; padding: 10px 0; border-bottom: 1px solid rgba(253,250,245,.08);
  display: flex; justify-content: space-between; align-items: center; transition: color 160ms;
  letter-spacing: .08em; text-transform: uppercase;
}
:global(.sh-drawer-nav a:hover) { color: #FDFAF5; }
:global(.sh-drawer-nav a svg) { color: rgba(253,250,245,.3); width: 14px; height: 14px; }
:global(.sh-drawer-resv-item) {
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; font-weight: 600; color: #E7BB7F;
  background: none; border: 0; border-bottom: 1px solid rgba(253,250,245,.08);
  padding: 10px 0; display: flex; justify-content: space-between; align-items: center;
  cursor: pointer; width: 100%; text-align: left; transition: color 160ms;
  letter-spacing: .08em; text-transform: uppercase;
}
:global(.sh-drawer-resv-item:hover) { color: #FDFAF5; }
:global(.sh-drawer-resv-item svg) { color: #E7BB7F; width: 14px; height: 14px; }
:global(.sh-drawer-lang-item) {
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(253,250,245,.8);
  padding: 10px 0; display: flex; align-items: center; gap: 8px; letter-spacing: .08em;
}
:global(.sh-drawer-lang-item a) {
  background: none; border: 0; font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 15px; color: rgba(253,250,245,.4); cursor: pointer; padding: 0; transition: color 160ms;
  letter-spacing: .08em;
  text-decoration: none;
}
:global(.sh-drawer-lang-item a.on) { color: #E7BB7F; font-weight: 600; }
:global(.sh-drawer-lang-item a:hover) { color: #FDFAF5; }
:global(.sh-drawer-lang-sep) { color: rgba(253,250,245,.2); }
:global(.sh-drawer-weather) {
  display: flex; gap: 16px; padding: 12px 0; border-top: 1px solid rgba(253,250,245,.08); margin-top: 4px;
}
:global(.sh-drawer-weather-item) {
  display: flex; align-items: center; gap: 6px;
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 14px; color: rgba(253,250,245,.7);
}
:global(.sh-drawer-weather-item svg) { color: #E7BB7F; }
:global(.sh-drawer-weather-sea svg) { color: #7ecde8; }

/* ─── Responsive ─────────────────────────────────────────── */
/* ─── Search button (topbar) ────────────────────────────── */
.sh-search-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; cursor: pointer;
  color: rgba(255,255,255,.7); padding: 4px 6px; border-radius: var(--radius-sm);
  transition: color var(--dur-fast), background var(--dur-fast);
}
.sh-search-btn:hover { color: #fff; background: rgba(255,255,255,.07); }
.sh-search-btn-label { font-size: 13px; letter-spacing: .06em; }

/* mobile icon-only button in hdr-right — hidden on desktop */
.sh-search-btn--hdr { display: none; color: #fff; padding: 4px; }

/* ─── Drawer search item ────────────────────────────────── */
:global(.sh-drawer-search-item) {
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; font-weight: 600;
  color: rgba(253,250,245,.6); background: none; border: 0;
  border-bottom: 1px solid rgba(253,250,245,.08);
  padding: 10px 0; display: flex; justify-content: space-between; align-items: center;
  cursor: pointer; width: 100%; text-align: left; transition: color 160ms;
  letter-spacing: .08em; text-transform: uppercase;
}
:global(.sh-drawer-search-item:hover) { color: #FDFAF5; }
:global(.sh-drawer-search-item svg) { color: rgba(253,250,245,.3); width: 14px; height: 14px; }

@media (max-width: 1200px) {
  .sh-nav { display: none; }
  .sh-hamburger { display: flex; }
  .sh-topbar { display: none; }
  .sh-search-btn--hdr { display: none; }
  .sh-hdr {
    padding: 35px 20px;
  }
  .sh-btn-reserve {
    min-width: 0;
    padding: 9px 12px;
    font-size: 11px;
    gap: 6px;
  }
}
@media (max-width: 1440px) {
  .sh-topbar {
  width: 95dvw;
  margin-inline: 2rem;
  padding: 9px 0px;
}
}

:global(.sh-drawer-accordion) {
  border-bottom: 1px solid rgba(253,250,245,.08);
}
:global(.sh-drawer-accordion-trigger) {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 17px; font-weight: 600;
  color: rgba(253,250,245,.8);
  background: none; border: 0; padding: 10px 0;
  width: 100%; text-align: left; cursor: pointer;
  display: flex; justify-content: space-between; align-items: center;
  letter-spacing: .08em; text-transform: uppercase;
  transition: color 160ms;
}
:global(.sh-drawer-accordion-trigger:hover) { color: #FDFAF5; }
:global(.sh-drawer-accordion-caret) {
  color: rgba(253,250,245,.3); width: 14px; height: 14px;
  transition: transform 220ms ease;
  flex-shrink: 0;
}
:global(.sh-drawer-accordion-caret.open) { transform: rotate(180deg); }
:global(.sh-drawer-accordion-body) {
  display: grid; grid-template-rows: 0fr;
  transition: grid-template-rows 220ms ease;
}
:global(.sh-drawer-accordion-body.open) { grid-template-rows: 1fr; }
:global(.sh-drawer-accordion-body > div) { overflow: hidden; }
:global(.sh-drawer-sub-link) {
  display: flex !important;
  flex-direction: column;
  align-items: flex-start !important;
  text-align: left;
  gap: 2px;
  font-family: 'Cormorant Garamond', Georgia, serif !important;
  font-size: 15px !important; font-weight: 500 !important;
  color: rgba(253,250,245,.6) !important;
  text-decoration: none; padding: 0 14px !important;
  border-bottom: none !important;
  transition: color 160ms;
}
:global(.sh-drawer-sub-link:hover) { color: #E7BB7F !important; }
:global(.sh-drawer-sub-title) {
  letter-spacing: .06em; text-transform: uppercase;
}
:global(.sh-drawer-sub-desc) {
  font-size: 12px !important; font-weight: 400 !important;
  text-transform: none; letter-spacing: normal;
  color: rgba(253,250,245,.4) !important;
}

</style>
